import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import { ENDPOINTS } from '../constants/endpoints';

export interface Department {
  id: number;
  name: string;
}

interface ListDepartmentsResponse {
  departments: Department[];
}

async function fetchDepartments(
  organizationId: number,
): Promise<ListDepartmentsResponse> {
  const params = new URLSearchParams({
    organizationId: organizationId.toString(),
  });

  const response = await apiFetch(
    `${ENDPOINTS.DEPARTMENT_LIST}?${params.toString()}`,
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch departments');
  }

  return response.json();
}

export function useDepartments(
  organizationId: number,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['department', 'list', organizationId],
    queryFn: () => fetchDepartments(organizationId),
    enabled,
  });
}

async function createDepartment(params: {
  name: string;
  organisationId: number;
}): Promise<Department> {
  const response = await apiFetch(ENDPOINTS.DEPARTMENT_CREATE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to create department');
  }

  return response.json();
}

export function useCreateDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDepartment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['department'] });
    },
  });
}

async function updateDepartment(params: {
  departmentId: number;
  name: string;
  organisationId: number;
}): Promise<Department> {
  const response = await apiFetch(
    ENDPOINTS.DEPARTMENT_UPDATE(params.departmentId),
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: params.name,
        organisationId: params.organisationId,
      }),
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to update department');
  }

  return response.json();
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateDepartment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['department'] });
    },
  });
}

async function deleteDepartment(params: {
  departmentId: number;
  organisationId: number;
}): Promise<void> {
  const response = await apiFetch(
    ENDPOINTS.DEPARTMENT_DELETE(params.departmentId, params.organisationId),
    { method: 'DELETE' },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to delete department');
  }
}

export function useDeleteDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteDepartment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['department'] });
    },
  });
}
